import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorText } from "@/lib/api";
import { PropertiesTable } from "@/components/onboarding/PropertiesTable";
import { BrandSettings } from "@/components/onboarding/BrandSettings";
import { useAuth } from "@/lib/auth";
import { IntegrationsPanel } from "@/components/onboarding/IntegrationsPanel";

export const MspHome = () => {
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState([]);
  const { refreshUser } = useAuth();
  const load = useCallback(() => {
    api.get("/msp/me").then((r) => {
      setMe(r.data);
      if (r.data.status === "approved") api.get("/msp/properties").then((p) => setRows(p.data)).catch((e) => toast.error(errorText(e)));
    }).catch((e) => toast.error(errorText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!me) return null;
  if (me.status !== "approved") return (
    <section className="max-w-xl rounded-lg border border-[#222D3D] bg-[#0F141C] p-6" data-testid="msp-pending-card">
      <Clock className="h-6 w-6 text-amber-400" />
      <h2 className="mt-4 text-2xl font-semibold text-slate-50">{me.status === "rejected" ? "Your application wasn't approved" : "Your application is being reviewed"}</h2>
      <p className="mt-2 text-sm text-slate-400">{me.status === "rejected" ? "Please get in touch if you think this is a mistake." : "We'll approve your MSP account shortly. Your branded sign-up page goes live as soon as we do."}</p>
    </section>
  );

  const link = `${window.location.origin}/p/${me.slug}`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Link copied"), () => toast.error("Couldn't copy, select the link instead"));
  return (
    <div className="space-y-10">
      <section className="rounded-lg border border-[#222D3D] bg-[#0F141C] p-6 space-y-3" data-testid="msp-link-card">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-emerald-400">Your property sign-up page</p>
        <p className="text-sm text-slate-400">Send this link to properties. It shows only your branding.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <code className="flex-1 min-w-0 truncate rounded-md border border-[#222D3D] bg-[#07090E] px-3 py-2 text-sm text-slate-200" data-testid="msp-portal-link">{link}</code>
          <Button onClick={copy} variant="outline" className="border-[#222D3D] bg-transparent" data-testid="msp-copy-link-btn"><Copy className="h-4 w-4 mr-2" />Copy</Button>
        </div>
      </section>
      <BrandSettings brand={me.brand} onSaved={() => { load(); refreshUser(); }} />
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-100">Your properties</h2>
        <PropertiesTable rows={rows} basePath="/msp/properties" onChange={load} />
      </section>
      <IntegrationsPanel msp />
    </div>
  );
};
